import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AuthProvider } from './lib/auth';
import { RequireAuthenticated } from './lib/RequireAuth';
import { api } from './lib/api';
import { AppShell } from './components/AppShell';
import { LandingPage } from './pages/LandingPage';
import { DashboardPage } from './pages/DashboardPage';
import { AuthPage } from './pages/AuthPage';
import { AgentsListPage } from './pages/AgentsListPage';
import { AgentBuilderPage } from './pages/AgentBuilderPage';
import { RunsListPage } from './pages/RunsListPage';
import { RunDetailPage } from './pages/RunDetailPage';
import { RunTranscriptPage } from './pages/RunTranscriptPage';
import { ApprovalsInboxPage } from './pages/ApprovalsInboxPage';
import { ApprovalDetailPage } from './pages/ApprovalDetailPage';
import { ConnectionsPage } from './pages/ConnectionsPage';
import { OAuthCallbackPage } from './pages/OAuthCallbackPage';
import { HubspotOAuthCallbackPage } from './pages/HubspotOAuthCallbackPage';
import { GoogleLoginCallbackPage } from './pages/GoogleLoginCallbackPage';
import { AdminPage } from './pages/AdminPage';
import { PlatformDashboardPage } from './pages/PlatformDashboardPage';
import { PollerActivityPage } from './pages/PollerActivityPage';
import { Gallery } from './ui/__gallery__/Gallery';

/**
 * TanStack Router (code-based). The root wraps the tree in <AuthProvider>; the
 * authenticated `/app` layout route guards access — a missing token redirects to
 * /login (checked in `beforeLoad`, before rendering), and <RequireAuthenticated> then
 * covers the present-but-invalid token case (clears the stale token + redirects to
 * /login) before the shell (AppBar + nav + SSE notification badge) wraps every child page.
 */

const rootRoute = createRootRoute({
  component: () => (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});
const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: () => <AuthPage mode="register" />,
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: () => <AuthPage mode="login" />,
});

/** Authed layout route. Redirects to /login when no token is present. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  beforeLoad: () => {
    if (!api.token) throw redirect({ to: '/login' });
  },
  component: AuthedShell,
});

function AuthedShell() {
  return (
    <RequireAuthenticated>
      <AppShell>
        <Outlet />
      </AppShell>
    </RequireAuthenticated>
  );
}

/**
 * Google OAuth redirect landing (issue #53). Top-level path so it can be registered
 * verbatim as the Google Cloud "Authorized redirect URI" (`/oauth/google/callback`).
 * Token-guarded like the app shell; it waits for `me` to load (so a workspace is
 * available) before the callback page exchanges the code.
 */
const oauthGoogleCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/google/callback',
  beforeLoad: () => {
    if (!api.token) throw redirect({ to: '/login' });
  },
  component: OAuthCallbackGate,
});

function OAuthCallbackGate() {
  return (
    <RequireAuthenticated>
      <OAuthCallbackPage />
    </RequireAuthenticated>
  );
}

/**
 * HubSpot OAuth redirect landing (WP-R6). Top-level path so it can be registered
 * verbatim as the HubSpot app "Redirect URL" (`/oauth/hubspot/callback`).
 * Token-guarded + waits for `me` like the Google mailbox-connect landing.
 */
const oauthHubspotCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/hubspot/callback',
  beforeLoad: () => {
    if (!api.token) throw redirect({ to: '/login' });
  },
  component: HubspotOAuthCallbackGate,
});

function HubspotOAuthCallbackGate() {
  return (
    <RequireAuthenticated>
      <HubspotOAuthCallbackPage />
    </RequireAuthenticated>
  );
}

/**
 * "Sign in with Google" redirect landing (issue #56). Top-level path so it can be
 * registered verbatim as the Google Cloud "Authorized redirect URI"
 * (`/oauth/google/login-callback`). NOT token-guarded — the user is being logged in
 * HERE; the page exchanges the code, stores the session, and then routes to /app.
 */
const googleLoginCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/google/login-callback',
  component: GoogleLoginCallbackPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'dashboard',
  component: DashboardPage,
});
const agentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'agents',
  component: AgentsListPage,
  validateSearch: validateAgentsSearch,
});
const agentBuilderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'agents/$agentId',
  component: AgentBuilderPage,
  // #326: carry the operator-selected workspace (`ws`) so a platform operator editing a
  // FOREIGN workspace's agent scopes the builder's reads + writes to that tenant. Ignored
  // for a non-operator (they never set it and it never leaves their own workspace).
  validateSearch: validateAgentsSearch,
});
/**
 * The runs-list filter search shape, persisted in the URL so it survives refresh, is
 * shareable, and — per #279 — can be CARRIED through the full-page run detail and the
 * transcript and back, so "Back to runs" returns the list exactly as the operator left it.
 */
export type RunsSearch = {
  agentId?: string;
  // #299: filter the list to a single email thread (from a run-detail "runs on this
  // thread" link). Rides the same filter-preserving hops as the other axes.
  threadId?: string;
  after?: string;
  before?: string;
  includeArchived?: boolean;
  outcome?: 'results' | 'no_action' | 'failed' | 'all';
  // #278: the operator-selected workspace to VIEW (absent = the caller's own). Rides the
  // same `search: (prev) => prev` hops as the filters, so an operator's workspace choice
  // survives list → full detail → transcript → Back. Ignored for a non-operator.
  ws?: string;
};

/**
 * Validate/normalise the runs filter search params. Shared by the runs list, the full-page
 * run detail, and the transcript (#279) so the same filter state rides every hop:
 *   - the "filter by agent" selection (#112) + the `createdAt` window (#130, ISO instants);
 *   - the "Include archived agents' runs" toggle (#215) — only `true` is carried;
 *   - the derived-outcome filter (#263) — default `results` is omitted from the URL.
 * Absent/blank values mean "no filter" on that axis.
 */
function validateRunsSearch(search: Record<string, unknown>): RunsSearch {
  const str = (v: unknown): string | undefined =>
    typeof v === 'string' && v.length > 0 ? v : undefined;
  const out: RunsSearch = {};
  if (str(search.agentId)) out.agentId = str(search.agentId);
  if (str(search.threadId)) out.threadId = str(search.threadId);
  if (str(search.after)) out.after = str(search.after);
  if (str(search.before)) out.before = str(search.before);
  if (search.includeArchived === true || search.includeArchived === 'true')
    out.includeArchived = true;
  if (search.outcome === 'no_action' || search.outcome === 'failed' || search.outcome === 'all')
    out.outcome = search.outcome;
  if (str(search.ws)) out.ws = str(search.ws);
  return out;
}

/** The Agents-list search shape (#278): the operator-selected workspace to VIEW. */
export type AgentsSearch = { ws?: string };

/**
 * Validate/normalise the Agents-list search params. Only `ws` (#278) — the operator's
 * selected workspace to view; a non-operator never sets it and it is ignored for them.
 */
function validateAgentsSearch(search: Record<string, unknown>): AgentsSearch {
  return typeof search.ws === 'string' && search.ws.length > 0 ? { ws: search.ws } : {};
}

const runsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'runs',
  component: RunsListPage,
  validateSearch: validateRunsSearch,
});
// #297: the FULL run detail (Outputs, full prompt, transcript link, approvals, cost) at
// PAGE WIDTH — a full-screen sibling route reached DIRECTLY from a runs-list row click
// (the earlier split-pane preview #279 is removed). It carries the runs filter search so
// its "Back to runs" returns the list exactly as the operator left it.
const runFullRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'runs/$runId/full',
  component: RunDetailPage,
  validateSearch: validateRunsSearch,
});
// The step-by-step transcript is a power-user "advanced" screen split off the run
// page (#120) so the run page can lead with Outputs. It stays a FULL-SCREEN sibling
// route (not nested in the split view) — a deeper view, reached via `view-transcript`.
// It carries the runs filter search (#279) so returning to the full detail (and then to
// the runs list) preserves the operator's filters.
const runTranscriptRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'runs/$runId/transcript',
  component: RunTranscriptPage,
  validateSearch: validateRunsSearch,
});
const approvalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'approvals',
  component: ApprovalsInboxPage,
});
// Approval detail is a CHILD of the approvals list route so the two render as a
// persistent SPLIT VIEW (Phase 3b, mirroring Runs): `ApprovalsInboxPage` owns the
// prioritized master list and renders `<Outlet/>` as the detail pane; navigating to
// `/app/approvals/$approvalId` fills that pane (desktop) or takes over full-screen
// (mobile) without unmounting the inbox.
const approvalDetailRoute = createRoute({
  getParentRoute: () => approvalsRoute,
  path: '$approvalId',
  component: ApprovalDetailPage,
});
const connectionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'connections',
  component: ConnectionsPage,
});
const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin',
  component: AdminPage,
});
// Operator-only cross-tenant Platform dashboard (#332). A sibling of the Admin page (linked
// from its operator-only card); the page itself probes the operator gate and shows a
// restricted notice — no totals — to a non-operator who reaches it by URL. No search params.
const platformDashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/platform',
  component: PlatformDashboardPage,
});
// Operator-only per-user poller troubleshooting lookup (#318). A sibling of the Admin page
// (linked from its operator-only poller section); the page itself probes the operator gate and
// shows a restricted notice — with no lookup surface — to a non-operator who reaches it by URL.
const pollerActivityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/poller-activity',
  component: PollerActivityPage,
});

const appIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/app/dashboard' });
  },
});

/**
 * Dev-only design-system gallery (`/ui-preview`). Registered ONLY under `vite dev`
 * (`import.meta.env.DEV`) so it never ships to production and is not in the app nav.
 */
const uiPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ui-preview',
  component: Gallery,
});

const devRoutes = import.meta.env.DEV ? [uiPreviewRoute] : [];

const routeTree = rootRoute.addChildren([
  indexRoute,
  registerRoute,
  loginRoute,
  ...devRoutes,
  oauthGoogleCallbackRoute,
  oauthHubspotCallbackRoute,
  googleLoginCallbackRoute,
  appRoute.addChildren([
    appIndexRoute,
    dashboardRoute,
    agentsRoute,
    agentBuilderRoute,
    runsRoute,
    runFullRoute,
    runTranscriptRoute,
    approvalsRoute.addChildren([approvalDetailRoute]),
    connectionsRoute,
    adminRoute,
    platformDashboardRoute,
    pollerActivityRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
